import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border border-input bg-background shadow-xs text-muted-foreground hover:bg-accent hover:text-foreground",
        "outline-destructive":
          "border border-destructive/50 bg-destructive/5 shadow-xs hover:bg-destructive/90 hover:text-accent text-destructive",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-transparent hover:text-accent-foreground !p-0",
        link: "text-primary underline-offset-4 hover:underline",
        outline_success: "bg-green-50 text-green-500 border-green-300 border hover:bg-green-100",
        outline_warning: "bg-yellow-50 text-yellow-500 border-yellow-500 border border-yellow-500",
        outline_error: "bg-red-50 text-red-500 border-red-500 border border-red-500",
        outline_info: "bg-blue-50 text-blue-400 border-blue-300 border hover:bg-blue-100 hover:text-blue-500",
        outline_update: "bg-purple-50 text-purple-400 border-purple-500 border border-purple-300 hover:bg-purple-100",
        outline_primary: "bg-primary/5 text-primary/80 border-primary border border-primary hover:bg-primary/10 hover:text-primary",
        success: "bg-green-500 text-white border-green-500 border border-green-500",
        warning: "bg-yellow-500 text-white border-yellow-500 border border-yellow-500",
        error: "bg-red-500 text-white border-red-500 border border-red-500",
        info: "bg-blue-500 text-white border-blue-500 border border-blue-500 hover:bg-blue-600",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export { Button, buttonVariants }
